import { useCart } from '@zonofi/zono-money-helpers';
import {
  getCustomerInvite,
  Product,
  ProductDetailsV2,
  ProductVariant,
  useAddItemToActiveOrderMutation,
  useAppSelector,
  useEditOrderMutation,
  useGetOrderDetailsMutation,
  useGetProductsByCustomerMutation,
  useGetTrackPODetailsMutation,
} from '@zonofi/zono-money-store';
import { isUndefined } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

export const useProductDetailsPageV2 = () => {
  const [searchParams] = useSearchParams();
  const fromSource = searchParams.get('source');
  const sourceOrderId = searchParams.get('orderId');
  const pvId = searchParams.get('pvId');
  const campaignId = searchParams.get('campaignId');
  const hideImages = searchParams.get('hImg') === 'true';
  const hidePrice = searchParams?.get('hp') === 'true';

  const params = useParams();
  const { productId } = params;

  const inviteData = useAppSelector(getCustomerInvite);
  const workspaceId = inviteData?.seller?.workspaceId;
  const customerId = inviteData?.inviteId;
  const { waConfig } = inviteData;

  const {
    callCartAPI,
    cartAPIResponse,
    productsInCart,
    cartSummaryAPIResponse,
    onDelete,
    editOrderResponse,
  } = useCart();

  const [activeProductVariant, setActiveProductVariant] =
    useState<ProductVariant>();
  const [activeProductVariantId, setActiveProductVariantId] = useState<number>(
    Number(pvId)
  );
  const [productImages, setProductImages] = useState<string[]>([]);
  const [productVariants, setProductVariants] = useState<ProductDetailsV2[]>(
    []
  );
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductDetailsV2 | null>(
    null
  );
  const [initialCartSnapshot, setInitialCartSnapshot] = useState<
    Record<number, number>
  >({});

  const [openDescriptionDrawer, setOpenDescriptionDrawer] =
    useState<boolean>(false);

  const [fetchOrderDetailsAPI, fetchOrderDetailAPIResponse] =
    useGetOrderDetailsMutation();
  const [fetchPoFileDetailsAPI, fetchPoFileDetailsAPIResponse] =
    useGetTrackPODetailsMutation();
  const [getProductDetails, getProductDetailsResponse] =
    useGetProductsByCustomerMutation();
  const [addItemToSubmittedOrderAPI, addItemToSubmittedOrderAPIResponse] =
    useEditOrderMutation();
  const [addToCartAPI, addToCartAPIResponse] =
    useAddItemToActiveOrderMutation();

  const isPoFile = useMemo(() => {
    return isNaN(Number(sourceOrderId));
  }, [sourceOrderId]);

  const hasCartChanges = useMemo(() => {
    return productVariants?.some((variant) => {
      const id = variant.productVariantId;
      const currentQty = variant.itemInCart?.quantity ?? 0;
      const initialQty = initialCartSnapshot[id] ?? 0;

      // newly staged
      if (variant.itemInCart?.staging) return true;

      // quantity changed
      if (currentQty !== initialQty) return true;

      return false;
    });
  }, [productVariants, initialCartSnapshot]);

  const deleteVariantFromCart = (variant: ProductDetailsV2) => {
    setEditingVariant(variant);
    onDelete(
      {
        sequence: '',
        orderId: variant?.itemInCart?.orderId ?? 0,
        skuCode: '',
        title: '',
        moq: 0,
        quantity: variant?.itemInCart?.quantity ?? 0,
        price: 0,
        newPrice: 0,
        total: 0,
        newQty: 0,
        productVariantId: variant?.productVariantId,
        deleted: false,
        isEdited: false,
        promotionNames: [],
        discountedPrice: 0,
        discountedTotal: 0,
        promotionIds: [],
      },
      workspaceId,
      customerId
    );
  };
  const formatTitle = (input: string) => {
    if (/[A-Z]/.test(input)) return input;
    // Otherwise, convert each space-separated word to Title Case.
    return input
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const isLoading = useMemo(() => {
    return (
      addToCartAPIResponse?.isLoading ||
      addItemToSubmittedOrderAPIResponse?.isLoading
    );
  }, [addToCartAPIResponse, addItemToSubmittedOrderAPIResponse]);

  const isEditing = useMemo(() => {
    return editOrderResponse?.isLoading;
  }, [editOrderResponse]);

  const onAddToCart = () => {
    const stagedVariants = productVariants?.filter(
      (variant) => variant?.itemInCart?.staging || variant?.itemInCart?.inCart
    );
    if (fromSource === 'order' && sourceOrderId) {
      let orderId = sourceOrderId;
      if (isPoFile) {
        orderId =
          fetchPoFileDetailsAPIResponse?.data?.lines
            ?.find((i) => i?.orderId)
            ?.orderId?.toString() || '';
      }
      addItemToSubmittedOrderAPI({
        workspaceId,
        body: {
          customerId,
          lines: stagedVariants?.map((variant) => {
            return {
              quantity: variant?.itemInCart?.quantity || 1,
              productVariantId: variant?.productVariantId || 0,
              operator: 'add',
            };
          }),
          orderId: orderId,
        },
      });
    } else {
      addToCartAPI({
        sellerWorkspaceId: workspaceId,
        customerId,
        source: 'platform',
        campaignId: campaignId || '',
        ...(sourceOrderId ? { orderId: sourceOrderId } : {}),
        lines: stagedVariants?.map((variant) => {
          return {
            quantity: variant?.itemInCart?.quantity || 1,
            productVariantId: variant?.productVariantId || 0,
            operator: 'add',
          };
        }),
      });
    }
  };

  const onOpenDescriptionDrawer = () => {
    setOpenDescriptionDrawer(true);
  };

  const onCloseDescriptionDrawer = () => {
    setOpenDescriptionDrawer(false);
  };

  const onStagingTheVariant = (variantId: number) => {
    setProductVariants((prev) =>
      prev.map((variant) => {
        if (
          variant?.productVariantId !== variantId ||
          variant?.itemInCart?.inCart
        ) {
          return variant;
        }

        return {
          ...variant,
          itemInCart: {
            quantity: variant?.details?.moq,
            staging: !variant?.itemInCart?.staging,
          },
        };
      })
    );
  };

  const onIncrementStagedVariant = (variantId: number) => {
    setProductVariants((prev) =>
      prev.map((variant) => {
        if (variant?.productVariantId !== variantId) return variant;

        return {
          ...variant,
          itemInCart: {
            ...variant?.itemInCart,
            quantity: Math.max(
              variant?.details?.moq,
              (variant?.itemInCart?.quantity || 0) + 1
            ),
          },
        };
      })
    );
  };

  const onDecrementStagedVariant = (variantId: number) => {
    setProductVariants((prev) =>
      prev.map((variant) => {
        if (variant?.productVariantId !== variantId) return variant;

        return {
          ...variant,
          itemInCart: {
            ...variant?.itemInCart,
            quantity: Math.max(
              variant?.details?.moq,
              (variant?.itemInCart?.quantity || 0) - 1
            ),
          },
        };
      })
    );
  };

  useEffect(() => {
    if (productId || activeProductVariantId) {
      getProductDetails({
        workspaceId: workspaceId,
        params: {
          customerId,
          sellerWorkspaceId: workspaceId,
        },
        body: {
          includeFacets: true,
          includeDivisions: false,
          includeCfas: false,
          ...(productId
            ? { productIds: [productId], groupByProduct: true }
            : { productVariantId: Number(activeProductVariantId) }),
        },
      });
    }
  }, [productId]);

  useEffect(() => {
    if (!productId && activeProductVariantId) {
      getProductDetails({
        workspaceId: workspaceId,
        params: {
          customerId,
          sellerWorkspaceId: workspaceId,
        },
        body: {
          includeFacets: true,
          includeDivisions: false,
          includeCfas: false,
          productVariantId: Number(activeProductVariantId),
        },
      });
    }
  }, [activeProductVariantId]);

  useEffect(() => {
    if (cartAPIResponse?.isSuccess) {
      setProductVariants((prev) =>
        prev?.map((variant) => {
          const match = productsInCart?.find(
            (l) => l.productVariantId === variant.productVariantId
          );

          if (!match) {
            return {
              ...variant,
              itemInCart: {
                orderId: 0,
                quantity: 0,
                inCart: false,
              },
            };
          }

          return {
            ...variant,
            itemInCart: {
              orderId: match.orderId,
              quantity: match.quantity || 0,
              inCart: true,
            },
          };
        })
      );
    }
  }, [productsInCart, cartAPIResponse]);

  useEffect(() => {
    if (
      addToCartAPIResponse?.isSuccess ||
      addItemToSubmittedOrderAPIResponse?.isSuccess
    ) {
      if (fromSource === 'order') {
        if (isPoFile) {
          fetchPoFileDetailsAPI({
            workspaceId,
            body: {
              pofileId: sourceOrderId || '',
              customerId,
              includePromotionData: false,
              includeInvoice: false,
              includePromotion: false,
              includeTax: false,
            },
          });
        } else {
          fetchOrderDetailsAPI({
            workspaceId: workspaceId,
            orderId: Number(sourceOrderId),
            body: {
              includeInvoice: false,
              includeTax: false,
              includeCustomer: false,
              includePromotions: false,
              customerId,
              includePromotionData: false,
            },
          });
        }
      } else {
        callCartAPI({
          customerId,
          workspaceId,
          sellerWorkspaceId: workspaceId,
        });
      }
    }
  }, [addToCartAPIResponse, addItemToSubmittedOrderAPIResponse]);

  useEffect(() => {
    if (editOrderResponse?.isSuccess) {
      setEditingVariant(null);
      if (fromSource === 'order') {
        if (isPoFile) {
          fetchPoFileDetailsAPI({
            workspaceId,
            body: {
              pofileId: sourceOrderId || '',
              customerId,
              includePromotionData: false,
              includeInvoice: false,
              includePromotion: false,
              includeTax: false,
            },
          });
        } else {
          fetchOrderDetailsAPI({
            workspaceId: workspaceId,
            orderId: Number(sourceOrderId),
            body: {
              includeInvoice: false,
              includeTax: false,
              includeCustomer: false,
              includePromotions: false,
              customerId,
              includePromotionData: false,
            },
          });
        }
      } else {
        callCartAPI({
          customerId,
          workspaceId,
          sellerWorkspaceId: workspaceId,
        });
      }
    }
  }, [editOrderResponse]);

  useEffect(() => {
    if (fetchOrderDetailAPIResponse?.isSuccess) {
      setProductVariants((prev) =>
        prev.map((variant) => {
          const match = fetchOrderDetailAPIResponse?.data?.lines?.find(
            (l) => l.productVariant?.id === variant.productVariantId
          );

          if (!match) return variant;

          return {
            ...variant,
            itemInCart: {
              orderId: match.orderId,
              quantity: match.quantity || 0,
              inCart: true,
            },
          };
        })
      );
    }
  }, [fetchOrderDetailAPIResponse]);

  useEffect(() => {
    if (fetchPoFileDetailsAPIResponse?.isSuccess) {
      setProductVariants((prev) =>
        prev.map((variant) => {
          const match = fetchPoFileDetailsAPIResponse?.data?.lines?.find(
            (l) => l.productVariantId === variant.productVariantId
          );

          if (!match) return variant;

          return {
            ...variant,
            itemInCart: {
              orderId: match.orderId,
              quantity: match.quantity || 0,
              inCart: true,
            },
          };
        })
      );
    }
  }, [fetchPoFileDetailsAPIResponse]);

  useEffect(() => {
    if (getProductDetailsResponse?.isSuccess) {
      let activeVariant =
        getProductDetailsResponse?.data?.products[0]?.productVariants[0];

      if (!isUndefined(activeProductVariantId)) {
        activeVariant =
          getProductDetailsResponse?.data?.products[0]?.productVariants?.filter(
            (i) => i?.productVariantId === activeProductVariantId
          )?.[0];
      }

      setProductImages(
        activeVariant?.assets?.map((asset) => asset?.redirectUrl || '') || []
      );

      const variants =
        getProductDetailsResponse?.data?.products[0]?.productVariants?.map(
          (variant) => {
            return {
              title: variant?.name,
              price: variant?.basePrice || 0,
              tags: variant?.categories || [],
              description: variant?.shortDescription || '-',
              promotions: variant?.promotions,
              details: {
                ptr: variant?.PTR?.toFixed(2),
                margin: (variant?.PTR - variant?.price)?.toFixed(2),
                moq: variant?.minOrderQty,
                weightedPrice: variant?.price,
                pricingStrategy: variant?.pricingStrategy,
              },
              images:
                variant?.assets?.map((asset) => asset?.redirectUrl || '') || [],
              productVariantId: variant?.productVariantId,
              facets: variant?.facets,
              segments: variant?.segments || [],
              stock: variant?.stock || 0,
            };
          }
        );
      setProductVariants(variants);
      setActiveProductVariant(activeVariant);

      if (fromSource === 'order') {
        if (isPoFile) {
          fetchPoFileDetailsAPI({
            workspaceId,
            body: {
              pofileId: sourceOrderId || '',
              customerId,
              includePromotionData: false,
              includeInvoice: false,
              includePromotion: false,
              includeTax: false,
            },
          });
        } else {
          fetchOrderDetailsAPI({
            workspaceId: workspaceId,
            orderId: Number(sourceOrderId),
            body: {
              includeInvoice: false,
              includeTax: false,
              includeCustomer: false,
              includePromotions: false,
              customerId,
              includePromotionData: false,
            },
          });
        }
      } else {
        callCartAPI({
          customerId,
          workspaceId,
          sellerWorkspaceId: workspaceId,
        });
      }
    }
  }, [getProductDetailsResponse]);

  useEffect(() => {
    if (pvId) {
      setActiveProductVariantId(Number(pvId));
    }
  }, [pvId]);

  useEffect(() => {
    if (openDescriptionDrawer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [openDescriptionDrawer]);

  useEffect(() => {
    if (cartAPIResponse?.isSuccess) {
      const snapshot: Record<number, number> = {};
      productsInCart?.forEach((item) => {
        snapshot[item.productVariantId] = item.quantity;
      });
      setInitialCartSnapshot(snapshot);
    }
  }, [cartAPIResponse]);

  return {
    activeProductVariant,
    customerId,
    deleteVariantFromCart,
    getProductDetailsResponse,
    hasCartChanges,
    hideImages,
    hidePrice,
    isLoading,
    onAddToCart,
    onDecrementStagedVariant,
    onDelete,
    onIncrementStagedVariant,
    onStagingTheVariant,
    orderId: sourceOrderId,
    productImages,
    productVariants,
    setShowImageGallery,
    showImageGallery,
    totalCartItems: cartSummaryAPIResponse?.data?.totalSKUCount ?? 0,
    waConfig,
    workspaceId,
    isEditing,
    editingVariant,
    openDescriptionDrawer,
    onOpenDescriptionDrawer,
    onCloseDescriptionDrawer,
    formatTitle,
  };
};
